import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = { name: "", metier: "", email: "", phone: "", tarif: "", notes: "" };

export default function IntermittentModal({ open, onOpenChange, item, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(item);

  useEffect(() => {
    if (open) setForm(item ? { ...empty, ...item } : empty);
  }, [open, item]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    const payload = { name: form.name, metier: form.metier, email: form.email, phone: form.phone, tarif: form.tarif, notes: form.notes };
    try {
      if (editing) await api.put(`/intermittents/${item.id}`, payload);
      else await api.post("/intermittents", payload);
      toast.success(editing ? "Régisseur mis à jour" : "Régisseur ajouté");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, props = {}) => (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`inter-field-${k}`} {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="intermittent-modal">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Modifier le régisseur" : "Nouveau régisseur"}</DialogTitle>
          <DialogDescription>Coordonnées et informations du régisseur.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {field("name", "Nom *")}
          {field("metier", "Métier", { placeholder: "Régie, Son, Lumière..." })}
          {field("email", "Email", { type: "email" })}
          {field("phone", "Téléphone")}
          {field("tarif", "Tarif / Cachet")}
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="inter-field-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="inter-save-button">
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
